import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash indication in shop transactions + admin mPIN reset
import LockResetIcon from '@mui/icons-material/LockReset';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const AllShops = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [userId, setUserId] = useState(null);
  const { can } = usePermissions();

  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all');
  const [pincodeFilter, setPincodeFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [stateHeadFilter, setStateHeadFilter] = useState('all');

  // Transaction Dialog Date Filter state
  const [txStartDate, setTxStartDate] = useState('');
  const [txEndDate, setTxEndDate] = useState('');

  // Confirmation Modal State for Block / Unblock with Reason
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [targetShopForBlock, setTargetShopForBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  // Edit Shop Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [shopToEdit, setShopToEdit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    businessName: '',
    sellerName: '',
    mobileNumber: '',
    emailId: '',
    shopType: '',
    mcc: '',
    pincode: '',
    district: '',
    address: '',
    dob: '',
    gender: '',
    pan: '',
    promoterAadhaar: '',
    settlementAccountNumber: '',
    settlementAccountIfsc: '',
  });

  const [mccOptions, setMccOptions] = useState([
    { code: '5411', name: 'Grocery Stores, Supermarkets' },
    { code: '5311', name: 'Department Stores' },
    { code: '5812', name: 'Eating Places, Restaurants' },
    { code: '5814', name: 'Fast Food Restaurants' },
    { code: '5912', name: 'Drug Stores and Pharmacies' },
    { code: '5999', name: 'Miscellaneous and Specialty Retail' },
    { code: '7011', name: 'Hotels, Motels, Resorts' },
    { code: '7230', name: 'Beauty and Barber Shops' },
    { code: '7299', name: 'Personal Services' },
    { code: '7399', name: 'Business Services' },
    { code: '8011', name: 'Doctors' },
    { code: '8062', name: 'Hospitals' },
    { code: '8099', name: 'Health Practitioners, Medical Services' },
    { code: '8299', name: 'Schools and Educational Services' },
    { code: '4121', name: 'Taxicabs and Rideshares' },
    { code: '4814', name: 'Telecommunication Services' },
    { code: '4900', name: 'Utilities' },
    { code: '5045', name: 'Computers and Peripherals' },
    { code: '5072', name: 'Hardware Equipment and Supplies' },
    { code: '5200', name: 'Home Supply Warehouse Stores' },
    { code: '5251', name: 'Hardware Stores' },
    { code: '5399', name: 'General Merchandise' },
    { code: '5499', name: 'Miscellaneous Food Stores' },
    { code: '5541', name: 'Service Stations' },
    { code: '5651', name: 'Family Clothing Stores' },
    { code: '5661', name: 'Shoe Stores' },
    { code: '5712', name: 'Furniture and Home Furnishings' },
    { code: '5722', name: 'Household Appliance Stores' },
    { code: '5732', name: 'Electronics Stores' },
    { code: '5944', name: 'Jewelry Stores' },
    { code: '5977', name: 'Cosmetic Stores' },
  ]);

  const [businessCategoryOptions] = useState([
    { id: '48', name: 'Sole Proprietary' },
    { id: '42', name: 'Individual' },
    { id: '41', name: 'Proprietary' },
    { id: '2', name: 'Partnership' },
    { id: '5', name: 'Private Company' },
    { id: '6', name: 'Public Company' },
    { id: '44', name: 'Trust' },
    { id: '7', name: 'Society' },
  ]);

  useEffect(() => {
    fetchMccOptions();
  }, []);

  const fetchMccOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/mcc`);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        const mapped = res.data
          .map((item) => ({
            code: String(item.mccCode || item.code || item.id || ''),
            name: item.editedDescription || item.subCategory || item.category || item.name || item.mcc || '',
          }))
          .filter((item) => item.code);
        setMccOptions(mapped);
      }
    } catch (e) {
      console.warn('Could not fetch MCC list from API, using default fallback list');
    }
  };

  // Detail View Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);

  // Transaction history dialog states
  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPaymentMode, setTxPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]

  // [CASH-MODE] Reset mPIN dialog (super admin only)
  const [mpinDialogOpen, setMpinDialogOpen] = useState(false);
  const [mpinTargetShop, setMpinTargetShop] = useState(null);
  const [mpinStatus, setMpinStatus] = useState(null); // { isMpinSet, status } | null
  const [mpinStatusLoading, setMpinStatusLoading] = useState(false);
  const [mpinResetting, setMpinResetting] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize]);

  const fetchShops = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/shops/${userId}`, {
        params: {
          progress: '6',
          page: 1,
          limit: 500,
        },
      });

      const shopsData = response.data.shops || [];
      const shopsWithSellNumber = shopsData.map((shop, index) => ({
        ...shop,
        id: shop._id || index,
        sellNumber: index + 1,
      }));

      setShops(shopsWithSellNumber);
      setTotalRows(response.data.totalShops || shopsData.length);
    } catch (error) {
      const errMsg =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error fetching shops';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShops([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const getMerchantDisplayId = (shop) => {
    if (!shop) return 'N/A';
    return (
      shop.merchantId ||
      shop.acemoneyUsercode ||
      shop.vpaCode ||
      shop.sellerIdentifier ||
      (shop._id ? `MER-${String(shop._id).slice(-6).toUpperCase()}` : 'N/A')
    );
  };

  const handleOpenBlockModal = (shop) => {
    if (!can('merchants.allShops.block')) return;
    setTargetShopForBlock(shop);
    setBlockReason('');
    setBlockModalOpen(true);
  };

  const handleConfirmBlockToggle = async () => {
    if (!targetShopForBlock) return;
    if (!can('merchants.allShops.block')) {
      setSnackbarMessage('Only SuperAdmin can block or unblock merchants');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setBlockModalOpen(false);
      return;
    }

    if (!blockReason.trim()) {
      setSnackbarMessage('Please enter a reason for status change');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const currentStatus = targetShopForBlock.status || 'active';
    const isBlocked = currentStatus === 'blocked' || currentStatus === 'inactive';
    const newStatus = isBlocked ? 'active' : 'blocked';

    try {
      await axios.put(`${API_BASE_URL}/shops/toggle-status/${targetShopForBlock._id}`, {
        status: newStatus,
        reason: blockReason,
      });
      setSnackbarMessage(
        `Merchant ${targetShopForBlock.businessName || targetShopForBlock.sellerName || ''} is now ${newStatus}`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchShops();
    } catch (error) {
      console.error('Error updating shop status:', error);
      const errMsg =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to update shop status';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setBlockModalOpen(false);
      setTargetShopForBlock(null);
      setBlockReason('');
    }
  };

  const handleOpenEditModal = (shop) => {
    if (!can('merchants.allShops.edit')) return;
    setShopToEdit(shop);
    setEditForm({
      businessName: shop.businessName || shop.shopName || '',
      sellerName: shop.sellerName || shop.ownerName || '',
      mobileNumber: shop.mobileNumber || shop.phone || '',
      emailId: shop.emailId || shop.email || '',
      shopType: shop.shopType || shop.bussiness || '48',
      mcc: shop.mcc || '5411',
      pincode: shop.pincode || '',
      district: shop.district || '',
      address: shop.address || shop.shopAddress || shop.addressLine1 || '',
      dob: shop.dob || '1990-01-01',
      gender: shop.gender || 'Male',
      pan: shop.pan || shop.panCard || '',
      promoterAadhaar: shop.promoterAadhaar || shop.aadhaar || '',
      settlementAccountNumber: shop.settlementAccountNumber || shop.accountNumber || '',
      settlementAccountIfsc: shop.settlementAccountIfsc || shop.ifscCode || '',
    });
    setEditModalOpen(true);
  };

  const handleSaveEditShop = async () => {
    if (!shopToEdit || !can('merchants.allShops.edit')) return;
    setEditLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.put(
        `${API_BASE_URL}/shops/update-acemoney/${shopToEdit._id}`,
        editForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (res.data && res.data.success) {
        setSnackbarMessage(res.data.message || 'Merchant details updated on AceMoney & local system');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setEditModalOpen(false);
        fetchShops();
      } else {
        const errMsg = res.data?.message || 'Failed to update merchant on AceMoney';
        showSnackbar(errMsg, 'error');
      }
    } catch (err) {
      console.error('Error editing shop:', err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Error updating shop details';
      showSnackbar(errMsg, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!can('merchants.allShops.export')) return;

    if (!filteredShops || filteredShops.length === 0) {
      setSnackbarMessage('No data available to export');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const headers = [
      'SL',
      'Merchant ID',
      'Merchant Name',
      'Shop Name',
      'Shop Type',
      'STATE',
      'DISTRICT',
      'CONSTITUENCY',
      'Pincode',
      'Current Advance Fund',
      'Q-Pay Center ID',
      'State Head ID',
      'Status',
    ];
    const csvRows = [headers.join(',')];

    filteredShops.forEach((s, idx) => {
      const sl = idx + 1;
      const mId = getMerchantDisplayId(s);
      const mName = s.sellerName || s.ownerName || 'N/A';
      const sName = s.businessName || s.shopName || 'N/A';
      const sType = s.shopType || s.bussiness || 'General';
      const state = s.stateCode || s.state || 'Kerala';
      const district = s.district || 'N/A';
      const village = s.village || s.constituency || s.city || s.taluk || 'N/A';
      const pincode = s.pincode || 'N/A';
      const advanceFund = s.serviceAdvance || s.advanceFund || 0;
      const qpayCenter = s.qpayCenterId || s.assignedQPayCenter || s.msbId || 'N/A';
      const stateHead = s.stateHeadId || s.assignedStateHead || 'N/A';
      const status = s.status || 'active';

      const row = [
        sl,
        `"${mId}"`,
        `"${mName}"`,
        `"${sName}"`,
        `"${sType}"`,
        `"${state}"`,
        `"${district}"`,
        `"${village}"`,
        `"${pincode}"`,
        `"₹${advanceFund}"`,
        `"${qpayCenter}"`,
        `"${stateHead}"`,
        `"${status}"`,
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `approved_merchants_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    showSnackbar('CSV exported successfully', 'success');
  };

  const handlePrint = () => {
    if (!can('merchants.allShops.print')) return;
    if (!filteredShops || filteredShops.length === 0) {
      showSnackbar('No merchant data to print', 'warning');
      return;
    }
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>All Approved Merchants Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; }
            h2 { text-align: center; margin-bottom: 20px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; font-size: 11px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            tr:nth-child(even) { background-color: #f8fafc; }
          </style>
        </head>
        <body>
          <h2>All Approved Merchants Report</h2>
          <table>
            <thead>
              <tr>
                <th>SL</th>
                <th>Merchant ID</th>
                <th>Merchant Name</th>
                <th>Shop Name</th>
                <th>Shop Type</th>
                <th>State</th>
                <th>District</th>
                <th>Constituency</th>
                <th>Pincode</th>
                <th>Advance Fund</th>
                <th>Q-Pay Center ID</th>
                <th>State Head ID</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredShops
                .map(
                  (s, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${getMerchantDisplayId(s)}</td>
                  <td>${s.sellerName || s.ownerName || 'N/A'}</td>
                  <td>${s.businessName || s.shopName || 'N/A'}</td>
                  <td>${s.shopType || s.bussiness || 'General'}</td>
                  <td>${s.stateCode || s.state || 'Kerala'}</td>
                  <td>${s.district || 'N/A'}</td>
                  <td>${s.village || s.constituency || s.city || s.taluk || 'N/A'}</td>
                  <td>${s.pincode || 'N/A'}</td>
                  <td>₹${s.serviceAdvance || s.advanceFund || 0}</td>
                  <td>${s.qpayCenterId || s.assignedQPayCenter || s.msbId || 'N/A'}</td>
                  <td>${s.stateHeadId || s.assignedStateHead || 'N/A'}</td>
                  <td>${s.status || 'active'}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() { window.print(); window.close(); };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleOpenDetails = (shop) => {
    if (!can('merchants.allShops.view')) return;
    setSelectedShop(shop);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedShop(null);
  };

  const handleOpenTxDialog = async (shop, mode = txPaymentMode) => { // [CASH-MODE] optional mode
    if (!shop) return;
    setTxLoading(true);
    setTxDialogOpen(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/shops/transactions/${shop._id}`, {
        params: { paymentMode: paymentModeParam(mode) }, // [CASH-MODE] undefined for ALL
      });
      setTransactions(res.data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setSnackbarMessage('Error loading transaction history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  const handleTxDialogClose = () => {
    setTxDialogOpen(false);
    setTransactions([]);
    setTxStartDate('');
    setTxEndDate('');
    setTxPaymentMode(PAYMENT_MODES.ALL); // [CASH-MODE]
  };

  // [CASH-MODE] refetch the shop's transactions for the selected mode
  const handleTxPaymentModeChange = (mode) => {
    setTxPaymentMode(mode);
    if (selectedShop) handleOpenTxDialog(selectedShop, mode);
  };

  // [CASH-MODE] Reset mPIN (super admin)
  const handleOpenMpinDialog = async (shop) => {
    if (!shop || !can('merchants.allShops.resetMpin')) return;
    setMpinTargetShop(shop);
    setMpinStatus(null);
    setMpinDialogOpen(true);
    setMpinStatusLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/cash/admin/shop/${shop._id}/mpin-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMpinStatus(res.data && res.data.success ? res.data.data : null);
    } catch (err) {
      setMpinStatus(null);
    } finally {
      setMpinStatusLoading(false);
    }
  };

  const handleCloseMpinDialog = () => {
    if (mpinResetting) return;
    setMpinDialogOpen(false);
    setMpinTargetShop(null);
    setMpinStatus(null);
  };

  const handleConfirmResetMpin = async () => {
    if (!mpinTargetShop || !can('merchants.allShops.resetMpin')) return;
    setMpinResetting(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/cash/admin/shop/${mpinTargetShop._id}/reset-mpin`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (res.data && res.data.success) {
        showSnackbar(res.data.message || 'mPIN reset successfully', 'success');
        setMpinDialogOpen(false);
        setMpinTargetShop(null);
        setMpinStatus(null);
      } else {
        showSnackbar((res.data && res.data.message) || 'Failed to reset mPIN', 'error');
      }
    } catch (err) {
      const errMsg =
        err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to reset mPIN';
      showSnackbar(errMsg, 'error');
    } finally {
      setMpinResetting(false);
    }
  };

  const handleExportTxCSV = () => {
    if (!can('merchants.allShops.export')) return;
    const listToExport = filteredTransactions;
    if (!listToExport || listToExport.length === 0) {
      showSnackbar('No transactions available to export', 'warning');
      return;
    }
    const headers = [
      'SL No',
      'Date & Time',
      'Merchant ID',
      'Customer ID',
      'Customer Type',
      'Customer Name',
      'Customer VPA ID',
      'Type',
      'Amount (INR)',
      'Commission %',
      'Commission Amount (INR)',
      'Merchant Amount (INR)',
      'Transaction ID',
      'Reference ID',
      'Status',
      'Mode' // [CASH-MODE]
    ];

    const csvRows = [headers.join(',')];

    listToExport.forEach((tx, idx) => {
      const sl = idx + 1;
      const dateVal = tx.dateTime || tx.createdAt || tx.date ? new Date(tx.dateTime || tx.createdAt || tx.date).toLocaleString('en-IN') : 'N/A';
      const mId = tx.merchantId || '';
      const cId = tx.customerId || '';
      const cType = tx.customerType || 'Customer';
      const cName = tx.customerName || '';
      const cVpa = can('merchants.allShops.viewSensitive') ? (tx.customerVPA || '') : hashVpa(tx.customerVPA);
      const type = tx.type || 'Credit';
      const amt = tx.amount || 0;
      const commPct = tx.commissionPercent || 0;
      const commAmt = tx.commissionAmount || 0;
      const merchAmt = tx.merchantAmount || 0;
      const txId = tx.transactionId || '';
      const refId = tx.referenceId || '';
      const status = tx.status || '';

      csvRows.push([
        sl,
        `"${dateVal}"`,
        `"${mId}"`,
        `"${cId}"`,
        `"${cType}"`,
        `"${cName}"`,
        `"${cVpa}"`,
        `"${type}"`,
        `"₹${amt}"`,
        `"${commPct}%"`,
        `"₹${commAmt}"`,
        `"₹${merchAmt}"`,
        `"${txId}"`,
        `"${refId}"`,
        `"${status}"`,
        `"${paymentModeLabel(tx)}"` // [CASH-MODE]
      ].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const shopTitle = (selectedShop?.businessName || selectedShop?.sellerName || 'Shop').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `${shopTitle}_Transactions_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // [CASH-MODE] client-side mode filter as a safety net (no-op for ALL)
  const filteredTransactions = filterRowsByMode(transactions, txPaymentMode).filter((tx) => {
    const dateVal = tx.dateTime || tx.createdAt || tx.date || tx.timestamp;
    if (!dateVal) return true;
    const txDate = new Date(dateVal);
    if (isNaN(txDate.getTime())) return true;

    // Compare IST calendar days as 'YYYY-MM-DD' strings so the browser time zone cannot shift the range
    const dayKey = txDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    if (txStartDate && dayKey < txStartDate) return false;
    if (txEndDate && dayKey > txEndDate) return false;
    return true;
  });

  // Hashing helpers for non-SuperAdmin logins
  const hashEmail = (email) => {
    if (!email || !email.includes('@')) return '****@****.com';
    const [user, domain] = email.split('@');
    return (user.length > 2 ? user.slice(0, 2) : user.slice(0, 1)) + '****@' + domain;
  };

  const hashPhone = (phone) => {
    if (!phone || phone.length < 6) return '******';
    return phone.slice(0, 3) + '****' + phone.slice(-3);
  };

  const hashAddress = (addr) => {
    if (!addr || addr === 'N/A') return 'N/A';
    return addr.slice(0, 4) + '****************';
  };

  const hashVpa = (vpa) => {
    if (!vpa || !vpa.includes('@')) return '****@vpa';
    const [handle, bank] = vpa.split('@');
    return (handle.length > 2 ? handle.slice(0, 2) : handle.slice(0, 1)) + '****@' + bank;
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // District name used for the filter: the backend sends districtCanonical (one name per district,
  // e.g. Trivandrum -> Thiruvananthapuram); fall back to the stored text for older responses.
  const shopDistrict = (s) => (s.districtCanonical || s.district || '').trim();

  // Extract unique values for filter dropdowns
  const uniqueDistricts = Array.from(new Set(shops.map(shopDistrict).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
  const uniqueVillages = Array.from(
    new Set(shops.map((s) => s.village || s.constituency || s.city || s.taluk).filter(Boolean))
  );
  const uniquePincodes = Array.from(new Set(shops.map((s) => s.pincode).filter(Boolean)));
  const uniqueQpayCenters = Array.from(
    new Set(shops.map((s) => s.qpayCenterId || s.assignedQPayCenter || s.msbId).filter(Boolean))
  );
  const uniqueStateHeads = Array.from(
    new Set(shops.map((s) => s.stateHeadId || s.assignedStateHead).filter(Boolean))
  );

  const filteredShops = shops.filter((shop) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const mId = getMerchantDisplayId(shop).toLowerCase();
      const bName = (shop.businessName || shop.shopName || '').toLowerCase();
      const sName = (shop.sellerName || shop.ownerName || '').toLowerCase();
      const mob = (shop.mobileNumber || shop.phoneNumber || '').toLowerCase();
      const pin = String(shop.pincode || '').toLowerCase();
      const email = (shop.emailId || shop.promoterEmail || '').toLowerCase();

      const matchesSearch =
        mId.includes(term) ||
        bName.includes(term) ||
        sName.includes(term) ||
        mob.includes(term) ||
        pin.includes(term) ||
        email.includes(term);
      if (!matchesSearch) return false;
    }

    if (districtFilter !== 'all') {
      if (shopDistrict(shop) !== districtFilter) return false;
    }

    if (villageFilter !== 'all') {
      const vil = shop.village || shop.constituency || shop.city || shop.taluk || '';
      if (vil !== villageFilter) return false;
    }

    if (pincodeFilter !== 'all') {
      if (String(shop.pincode) !== String(pincodeFilter)) return false;
    }

    if (qpayCenterFilter !== 'all') {
      const qid = shop.qpayCenterId || shop.assignedQPayCenter || shop.msbId || '';
      if (qid !== qpayCenterFilter) return false;
    }

    if (stateHeadFilter !== 'all') {
      const shid = shop.stateHeadId || shop.assignedStateHead || '';
      if (shid !== stateHeadFilter) return false;
    }

    return true;
  });

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 65,
      sortable: false,
      renderCell: (params) => {
        const index = filteredShops.findIndex(
          (s) => (s.id || s._id) === (params.row.id || params.row._id)
        );
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Actions', // placed before Merchant ID so actions are visible without horizontal scroll
      width: 280, // [CASH-MODE] was 240; room for Reset mPIN
      sortable: false,
      renderCell: (params) => {
        const currentStatus = params.row.status || 'active';
        const isBlocked = currentStatus === 'blocked' || currentStatus === 'inactive';

        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {can('merchants.allShops.view') && (
              <Tooltip title="View Merchant Details">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleOpenDetails(params.row)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {can('merchants.allShops.edit') && (
              <Tooltip title="Edit Merchant">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenEditModal(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {can('merchants.allShops.block') && (
                <Button
                  variant="contained"
                  size="small"
                  color={isBlocked ? 'success' : 'error'}
                  startIcon={isBlocked ? <CheckCircleOutlineIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                  onClick={() => handleOpenBlockModal(params.row)}
                  sx={{ fontWeight: 700, py: 0.3, px: 1, fontSize: '0.75rem' }}
                >
                  {isBlocked ? 'Unblock' : 'Block'}
                </Button>
            )}

            {/* [CASH-MODE] Reset shop mPIN */}
            {can('merchants.allShops.resetMpin') && (
                <Tooltip title="Reset mPIN">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => handleOpenMpinDialog(params.row)}
                  >
                    <LockResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {getMerchantDisplayId(params.row)}
        </Typography>
      ),
    },
    {
      field: 'sellerName',
      headerName: 'Merchant Name',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || params.row.ownerName || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'businessName',
      headerName: 'Shop Name',
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorefrontIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
            {params.value || params.row.shopName || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'shopType',
      headerName: 'Shop Type',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.bussiness || 'General'}
        </Typography>
      ),
    },
    {
      field: 'stateCode',
      headerName: 'STATE',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || params.row.state || 'Kerala'}
        </Typography>
      ),
    },
    {
      field: 'district',
      headerName: 'DISTRICT',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {shopDistrict(params.row) || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'village',
      headerName: 'CONSTITUENCY',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.constituency || params.row.city || params.row.taluk || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'pincode',
      headerName: 'Pincode',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'advanceFund',
      headerName: 'Current Advance Fund',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
          ₹{params.row.serviceAdvance || params.row.advanceFund || 0}
        </Typography>
      ),
    },
    {
      field: 'qpayCenterId',
      headerName: 'Q-Pay Center ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0284c7' }}>
          {params.row.qpayCenterId || params.row.assignedQPayCenter || params.row.msbId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateHeadId',
      headerName: 'State Head ID',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
          {params.row.stateHeadId || params.row.assignedStateHead || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const currentStatus = params.row.status || 'active';
        const isBlocked = currentStatus === 'blocked' || currentStatus === 'inactive';
        return (
          <Chip
            label={isBlocked ? 'Blocked' : 'Active'}
            color={isBlocked ? 'error' : 'success'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ position: 'relative', width: '100%', p: { xs: 2, sm: 3 } }}>
      <BackButton />
      <br />

      {/* Header & Filter Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justify: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                backgroundColor: 'rgba(255, 165, 0, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <StorefrontIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  All Approved Merchants
                </Typography>
                <Chip
                  label={`${filteredShops.length} Shown / ${totalRows} Total`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, borderRadius: 1.5 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                List of registered and approved Q-Pay Merchants
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {can('merchants.allShops.print') && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{ fontWeight: 700 }}
                >
                  Print
                </Button>
            )}
            {can('merchants.allShops.export') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  sx={{ fontWeight: 700 }}
                >
                  CSV
                </Button>
            )}
            <IconButton
              onClick={fetchShops}
              color="primary"
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 1,
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.08)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Filter Controls */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Name, ID, Mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2.2}>
            <FormControl fullWidth size="small">
              <InputLabel>District</InputLabel>
              <Select
                value={districtFilter}
                label="District"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {uniqueDistricts.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2.2}>
            <FormControl fullWidth size="small">
              <InputLabel>Constituency</InputLabel>
              <Select
                value={villageFilter}
                label="Constituency"
                onChange={(e) => setVillageFilter(e.target.value)}
              >
                <MenuItem value="all">All Constituencies</MenuItem>
                {uniqueVillages.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Pincode</InputLabel>
              <Select
                value={pincodeFilter}
                label="Pincode"
                onChange={(e) => setPincodeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {uniquePincodes.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Q-Pay Center</InputLabel>
              <Select
                value={qpayCenterFilter}
                label="Q-Pay Center"
                onChange={(e) => setQpayCenterFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {uniqueQpayCenters.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={1.6}>
            <FormControl fullWidth size="small">
              <InputLabel>State Head ID</InputLabel>
              <Select
                value={stateHeadFilter}
                label="State Head ID"
                onChange={(e) => setStateHeadFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {uniqueStateHeads.map((sh) => (
                  <MenuItem key={sh} value={sh}>
                    {sh}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid Container */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredShops}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pagination
          getRowId={(row) => row._id || row.id}
          loading={loading}
          disableSelectionOnClick
          rowHeight={60}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              fontWeight: 700,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Box>

      {/* Detailed Merchant View Modal (29 Requested Fields) */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StorefrontIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Merchant Shop Details
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {selectedShop && (
            <Grid container spacing={2}>
              {/* 1. Show Merchant ID */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Merchant ID:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                  {getMerchantDisplayId(selectedShop)}
                </Typography>
              </Grid>

              {/* 2. Show Merchant Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Merchant Name:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.sellerName || selectedShop.ownerName || 'N/A'}</Typography>
              </Grid>

              {/* 3. Show Shop Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Name:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.businessName || selectedShop.shopName || 'N/A'}</Typography>
              </Grid>

              {/* 4. Show Shop Email */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Email:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0369a1' }}>
                  {can('merchants.allShops.viewSensitive') ? (selectedShop.emailId || selectedShop.promoterEmail || 'N/A') : hashEmail(selectedShop.emailId || selectedShop.promoterEmail)}
                </Typography>
              </Grid>

              {/* 5. Show Shop Mobile */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Mobile:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {can('merchants.allShops.viewSensitive') ? (selectedShop.mobileNumber || selectedShop.phoneNumber || selectedShop.promoterWhatsApp || 'N/A') : hashPhone(selectedShop.mobileNumber || selectedShop.phoneNumber || selectedShop.promoterWhatsApp)}
                </Typography>
              </Grid>

              {/* 6. Show Shop Type */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Type:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.shopType || selectedShop.bussiness || 'General'}</Typography>
              </Grid>

              {/* 7. Show Shop Ownership Type */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Ownership Type:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.ownershipType || selectedShop.businessType || 'Proprietorship'}</Typography>
              </Grid>

              {/* 8. Show Shop Address */}
              <Grid item xs={12} sm={6} md={8}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Address:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {can('merchants.allShops.viewSensitive')
                    ? `${selectedShop.addressLine1 || ''} ${selectedShop.addressLine2 || ''} ${selectedShop.address || ''}`.trim() || 'N/A'
                    : hashAddress(`${selectedShop.addressLine1 || ''} ${selectedShop.addressLine2 || ''} ${selectedShop.address || ''}`.trim() || 'N/A')}
                </Typography>
              </Grid>

              {/* 9. Show Shop State */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop State:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.stateCode || selectedShop.state || 'Kerala'}</Typography>
              </Grid>

              {/* 10. Show Shop District */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop District:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.district || 'N/A'}</Typography>
              </Grid>

              {/* 11. Show Shop Pincode */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Pincode:</Typography>
                <Chip label={selectedShop.pincode || 'N/A'} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, mt: 0.3 }} />
              </Grid>

              {/* 12. Show Shop Latitude */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Latitude:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.latitude || selectedShop.lat || 'N/A'}</Typography>
              </Grid>

              {/* 13. Show Shop Longitude */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Longitude:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.longitude || selectedShop.lng || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* 14. Show Shop assigned State head */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop assigned State head:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>{selectedShop.stateHeadId || selectedShop.assignedStateHead || 'N/A'}</Typography>
              </Grid>

              {/* 15. Show Shop assigned Q-Pay Center */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop assigned Q-Pay Center:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0284c7' }}>{selectedShop.qpayCenterId || selectedShop.assignedQPayCenter || selectedShop.msbId || 'N/A'}</Typography>
              </Grid>

              {/* 16. Show Shop assigned Q-Pay Agent */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop assigned Q-Pay Agent:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{selectedShop.assignedQPayAgent || selectedShop.agentId || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* 17. Show Shop Bank Name */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Bank Name:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.shopBank || 'N/A'}</Typography>
              </Grid>

              {/* 18. Show Shop Bank Branch */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Bank Branch:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.shopBranch || 'N/A'}</Typography>
              </Grid>

              {/* 19. Show Shop Bank A/C No */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Bank A/C No:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.settlementAccountNumber || selectedShop.accountNumber || 'N/A'}</Typography>
              </Grid>

              {/* 20. Show Shop Bank IFSC Code */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Bank IFSC Code:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.settlementAccountIfsc || selectedShop.ifscCode || 'N/A'}</Typography>
              </Grid>

              {/* 21. Show Shop MCC */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop MCC:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.mcc || 'N/A'}</Typography>
              </Grid>

              {/* 22. Show Shop Status */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Status:</Typography>
                <Chip
                  label={selectedShop.status || 'active'}
                  color={(selectedShop.status === 'active' || selectedShop.status === 'Active') ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Grid>

              {/* 23. Show Shop Advance Fund */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Advance Fund:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                  ₹{selectedShop.serviceAdvance || selectedShop.advanceFund || 0}
                </Typography>
              </Grid>

              {/* 24. Show Shop Commission % */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Commission %:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.commissionRate || 0}%</Typography>
              </Grid>

              {/* 25. Show Shop Minimum Bill Amount */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Minimum Bill Amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{selectedShop.minimumPay || 0}</Typography>
              </Grid>

              {/* Optional maximum bill amount for game cards */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Maximum Bill Amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.maximumPayEnabled ? `₹${selectedShop.maximumPay || 0} (Active)` : 'Inactive'}</Typography>
              </Grid>

              {/* 26. Show Shop Cashback Count */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Cashback Count:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.cashbackCount || 0}</Typography>
              </Grid>

              {/* 27. Show Shop Wallet Warning Threshold */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Wallet Warning Threshold:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{selectedShop.warningThreshold || 600}</Typography>
              </Grid>

              {/* 28. Show Shop Block Threshold */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Shop Block Threshold:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{selectedShop.blockThreshold || 500}</Typography>
              </Grid>

              {/* 29. Show Transaction History with Eye Button */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, backgroundColor: '#f0f9ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #bae6fd' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>Show Transaction History:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      Total Sales: ₹{selectedShop.totalSales || 0}
                    </Typography>
                  </Box>
                  <Tooltip title="View Transaction History">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenTxDialog(selectedShop)}
                      sx={{ fontWeight: 700 }}
                    >
                      View Transaction History
                    </Button>
                  </Tooltip>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDialogClose} variant="contained" color="primary" sx={{ fontWeight: 700 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal for Block / Unblock with Reason */}
      <Dialog
        open={blockModalOpen}
        onClose={() => setBlockModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Confirm Merchant Block / Unblock
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ color: '#334155', fontWeight: 500, mb: 2 }}>
            {targetShopForBlock && (
              <>
                Are you sure you want to{' '}
                <strong>
                  {(targetShopForBlock.status || 'active') === 'blocked' || (targetShopForBlock.status || 'active') === 'inactive'
                    ? 'unblock'
                    : 'block'}
                </strong>{' '}
                Merchant <strong>{targetShopForBlock.businessName || targetShopForBlock.sellerName || ''}</strong>?
              </>
            )}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Block / Unblock"
            placeholder="Enter reason for changing merchant status..."
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            required
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBlockModalOpen(false)} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBlockToggle}
            color={
              targetShopForBlock &&
              (targetShopForBlock.status === 'blocked' || targetShopForBlock.status === 'inactive')
                ? 'success'
                : 'error'
            }
            variant="contained"
            sx={{ fontWeight: 700 }}
            disabled={!blockReason.trim()}
          >
            Confirm Status Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Shop Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => !editLoading && setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Edit Merchant Details (AceMoney MSB v1.2 Gateway Sync)
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shop Name *"
                value={editForm.businessName}
                onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Merchant Name *"
                value={editForm.sellerName}
                onChange={(e) => setEditForm({ ...editForm, sellerName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number (10 digits) *"
                value={editForm.mobileNumber}
                onChange={(e) => setEditForm({ ...editForm, mobileNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email ID *"
                value={editForm.emailId}
                onChange={(e) => setEditForm({ ...editForm, emailId: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Date of Birth (YYYY-MM-DD) *"
                value={editForm.dob}
                onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
                placeholder="1990-01-01"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Gender *"
                value={editForm.gender}
                onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode (6 digits) *"
                value={editForm.pincode}
                onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="District *"
                value={editForm.district}
                onChange={(e) => setEditForm({ ...editForm, district: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shop Address *"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number (Format: ABCDE1234F) *"
                value={editForm.pan}
                onChange={(e) => setEditForm({ ...editForm, pan: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Aadhaar Number (12 digits) *"
                value={editForm.promoterAadhaar}
                onChange={(e) => setEditForm({ ...editForm, promoterAadhaar: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Settlement Account Number *"
                value={editForm.settlementAccountNumber}
                onChange={(e) => setEditForm({ ...editForm, settlementAccountNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Settlement IFSC Code *"
                value={editForm.settlementAccountIfsc}
                onChange={(e) => setEditForm({ ...editForm, settlementAccountIfsc: e.target.value.toUpperCase() })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category / Business Ownership Type *"
                value={editForm.shopType}
                onChange={(e) => setEditForm({ ...editForm, shopType: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Business Category</option>
                {businessCategoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.id} - {cat.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="MCC (Merchant Category Code) *"
                value={editForm.mcc}
                onChange={(e) => setEditForm({ ...editForm, mcc: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select MCC Code</option>
                {mccOptions.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.code} - {m.name}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditModalOpen(false)} color="inherit" variant="outlined" disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditShop}
            color="primary"
            variant="contained"
            disabled={editLoading}
            sx={{ fontWeight: 700, minWidth: 140 }}
          >
            {editLoading ? <CircularProgress size={24} color="inherit" /> : 'Save & Sync AceMoney'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog
        open={txDialogOpen}
        onClose={handleTxDialogClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VisibilityIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Transaction History - {selectedShop ? selectedShop.businessName || selectedShop.sellerName : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {can('merchants.allShops.export') && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportTxCSV}
              disabled={filteredTransactions.length === 0}
              sx={{ fontWeight: 700 }}
            >
              Export CSV
            </Button>
            )}
            <IconButton onClick={handleTxDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 2 }}>
          {/* Date Filter Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  value={txStartDate}
                  onChange={(e) => setTxStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  value={txEndDate}
                  onChange={(e) => setTxEndDate(e.target.value)}
                />
              </Grid>
              {/* [CASH-MODE] Payment Mode filter (date fields narrowed from sm=4 to sm=3) */}
              <Grid item xs={12} sm={2}>
                <PaymentModeFilter value={txPaymentMode} onChange={handleTxPaymentModeChange} fullWidth sx={{ minWidth: 0 }} />
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setTxStartDate('');
                    setTxEndDate('');
                  }}
                  disabled={!txStartDate && !txEndDate}
                  sx={{ fontWeight: 600, height: 40 }}
                >
                  Reset Filter
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, ml: 'auto' }}>
                  Showing {filteredTransactions.length} of {transactions.length} transactions
                </Typography>
              </Grid>
            </Grid>
            {/* [CASH-MODE] UPI vs Cash for the rows shown */}
            <PaymentModeTotals totals={totalsByModeFromRows(filteredTransactions, (t) => t.amount)} amountKey="amount" title="Transactions" sx={{ mb: 0 }} />
          </Paper>

          {txLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>SL No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Merchant ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer VPA ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Commission %</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Commission amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Merchant amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reference ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    {/* [CASH-MODE] */}
                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} align="center" sx={{ py: 3 }}>{/* [CASH-MODE] Mode + Customer Type columns */}
                        <Typography variant="body2" color="text.secondary">
                          No transaction history found for this shop in selected date range
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.map((tx, index) => (
                      <TableRow key={tx.id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {tx.dateTime || tx.createdAt || tx.date
                            ? new Date(tx.dateTime || tx.createdAt || tx.date).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true,
                              })
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{tx.merchantId}</TableCell>
                        <TableCell>{tx.customerId}</TableCell>
                        <TableCell>
                          <Chip
                            label={tx.customerType || 'Customer'}
                            size="small"
                            color={(tx.customerType || 'Customer') === 'Non-Customer' ? 'default' : 'success'}
                            variant={(tx.customerType || 'Customer') === 'Non-Customer' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>{tx.customerName}</TableCell>
                        <TableCell>
                          {can('merchants.allShops.viewSensitive') ? tx.customerVPA : hashVpa(tx.customerVPA)}
                        </TableCell>
                        <TableCell>
                          <Chip label={tx.type || 'Credit'} color="info" size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>₹{tx.amount}</TableCell>
                        <TableCell>{tx.commissionPercent}%</TableCell>
                        <TableCell>₹{Number(tx.commissionAmount || 0).toFixed(4)}</TableCell>
                        <TableCell>₹{Number(tx.merchantAmount || 0).toFixed(4)}</TableCell>
                        <TableCell>{tx.transactionId}</TableCell>
                        <TableCell>{tx.referenceId}</TableCell>
                        <TableCell>
                          <Chip
                            label={tx.status}
                            color={
                              tx.status === 'SUCCESS'
                                ? 'success'
                                : tx.status === 'FAILED'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        {/* [CASH-MODE] */}
                        <TableCell>
                          <PaymentModeChip row={tx} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleTxDialogClose} variant="contained" color="primary" sx={{ fontWeight: 700 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* [CASH-MODE] Reset mPIN confirmation (super admin only) */}
      <Dialog open={mpinDialogOpen} onClose={handleCloseMpinDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'warning.dark' }}>
          Reset Shop mPIN
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ color: '#334155', fontWeight: 500, mb: 2 }}>
            {mpinTargetShop && (
              <>
                Reset the mPIN for{' '}
                <strong>{mpinTargetShop.businessName || mpinTargetShop.sellerName || ''}</strong>
                {mpinTargetShop.merchantId ? ` (${mpinTargetShop.merchantId})` : ''}? The shop will be asked to set a new
                mPIN at next login.
              </>
            )}
          </DialogContentText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
              Current mPIN status:
            </Typography>
            {mpinStatusLoading ? (
              <CircularProgress size={16} />
            ) : mpinStatus ? (
              <Chip
                size="small"
                label={mpinStatus.isMpinSet ? 'Set' : 'Not set'}
                color={mpinStatus.isMpinSet ? 'success' : 'default'}
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Unavailable</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseMpinDialog} color="inherit" variant="outlined" disabled={mpinResetting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmResetMpin}
            color="warning"
            variant="contained"
            sx={{ fontWeight: 700 }}
            disabled={mpinResetting}
          >
            {mpinResetting ? <CircularProgress size={22} color="inherit" /> : 'Reset mPIN'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllShops;
